import { QueryResult } from '@apollo/client/react';
import { merge } from 'lodash-es';
import { create } from 'zustand';

import { DeclineFeedback, SuggestionsTrigger } from '@pflegia/types/common/enums';

/**
 * Data structure for the match dialogue modal store.
 */

export type SuggestionModalStoreData = {
  open: boolean;
  /** Runs once after the suggestions modal closes. */
  afterClose?: () => void;
  trigger?: SuggestionsTrigger;
  initialNavigation?: string;
  jobPositionId?: string;
  title?: string;
  feedbackItems?: DeclineFeedback[];
  unblockAt?: Date;
  hadInteraction?: boolean;
  /**
   * Whether to show the modal after the current modal has been closed.
   */
  showAfterModalClose?: boolean;
  queryResult?: QueryResult<{ jobPositions: unknown[] }>;
};

/**
 * The default state for the match dialogue modal store.
 */
const DEFAULT_STATE: SuggestionModalStoreData = {
  open: false,
  afterClose: undefined,
  jobPositionId: undefined,
  feedbackItems: undefined,
  initialNavigation: undefined,
  queryResult: undefined,
  hadInteraction: undefined,
  showAfterModalClose: false,
  title: undefined,
  trigger: undefined,
  unblockAt: undefined,
} as const;

/**
 * Functions for the match dialogue modal store.
 */
export type SuggestionsModalStoreCallbacks = {
  /** Opens the modal with the provided properties. */
  onOpen: (props?: Partial<SuggestionModalStoreData>) => void;
  /** Closes the modal and resets the state to default. */
  onClose: () => void;
  /** Sets a specific property in the store. e.g update `trigger` */
  set: <K extends keyof SuggestionsModalStoreState, V = SuggestionsModalStoreState[K]>(key: K, value: V) => void;
};

/**
 * The state and actions for the match dialogue modal store.
 */
export type SuggestionsModalStoreState = (SuggestionModalStoreData & SuggestionsModalStoreCallbacks) & {
  /** Sets a specific property in the store. e.g update open */
  set: <K extends keyof SuggestionsModalStoreState, V = SuggestionsModalStoreState[K]>(key: K, value: V) => void;
};

/**
 * Hook for the match dialogue modal state and actions.
 */
export const useSuggestionsModalStore = create<SuggestionsModalStoreState>((set, get) => {
  const defaultCallbacks: SuggestionsModalStoreCallbacks = {
    onOpen: props => set(state => ({ ...merge({}, state, props), open: true })),
    onClose: () => {
      const { afterClose } = get();
      set(DEFAULT_STATE);
      afterClose?.();
    },
    set: (key, value) => set(state => ({ ...state, [key]: value })),
  };

  return {
    ...DEFAULT_STATE,
    ...defaultCallbacks,
  };
});
